/**
 * src/MainPage/AuditionForm.jsx
 *
 * This component:
 *  1. Collects a YouTube audition link.
 *  2. Lets the user select an instrument.
 *  3. Displays an embedded card form for payment.
 *  4. Creates a PaymentIntent via a Firebase HTTP endpoint "api/createPaymentIntent".
 *  5. Confirms the card payment on the client.
 *  6. Updates the Firestore reservation doc with the audition link and marks auditionPaid as true.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';

const defaultConcertRepertoire = [
  "We Wish You a Merry Christmas – A festive classic to start the celebration.",
  "Jingle Bell Rock – A cheerful, upbeat holiday favorite.",
  "White Christmas – A timeless melody evoking the magic of the season.",
  "Original Composition – An exclusive piece written for this special tour.",
  "Carol of the Bells – A powerful, iconic arrangement full of energy.",
  "Emmanuel – A serene and reflective holiday hymn.",
  "A Christmas Festival – A vibrant medley celebrating the joy of Christmas.",
  "Feliz Navidad – A lively, international Christmas classic to close the show.",
];

const defaultInstrumentExcerpts = {
  "Violin I": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 159 to 167, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/1xAuCg_lOueg53Fw90D_bcOo5RpW_c2QJ/view?usp=sharing"
  },
  "Violin II": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 159 to 167, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/14UiXSjWGQSUSDoBDJ-A_BJK0NPH16pkF/view?usp=sharing"
  },
  "Viola": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 83 to 126, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/17oZYL-j8fwj1moZGEi6W7XZ6lj-ioZ2W/view?usp=sharing"
  },
  "Cello": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 2 to 10 & 54 to 71, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/1lq05tq7tiQ8iCfvZPzTjDRC4WcUxaKjK/view?usp=sharing"
  },
  "Bass": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 2 to 10 & 54 to 71, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/19pGjXG1qTmfCBzgVXh9P30Eju4asZZg0/view?usp=sharing"
  },
  "Flauta": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 159 to 167, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/1btMfI2AQWxOzzGgtP0SxvKIEN-q8-ffW/view?usp=sharing"
  },
  "Oboe": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 138 to 151, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/1svP42xG13LHorKXx-rj6GKGhPY9Ck-0j/view?usp=sharing"
  },
  "Clarinete": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 159 to 167, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/1ZoCnnJO-HfIuaPIcZxQR-ZJZNebUZ2h9/view?usp=sharing"
  },
  "Basson": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 35 to 70, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/1Xptcat0peL44-AVpnCPJbM8ObUE7NcLC/view?usp=sharing"
  },
  "Saxo": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 159 to 167, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/drive/folders/your_saxo_folder_id"
  },
  "Corno": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 83 to 126, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/1rRMAmzOoXHZScWKeecZvKH1_gdpiVk_v/view?usp=sharing"
  },
  "Trompeta": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 137 to 152, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/15LXOCPtOw7t0aDFuGuyKltZurabO0o-w/view?usp=sharing"
  },
  "Trombone": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 11 to 33, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/1NgVBnMB-KIQBJWBQVmmva721emuFV-lQ/view?usp=sharing"
  },
  "Tuba": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 54 to 71, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/1C3bQYCuaS1JQ_3oX7xVUYq7pA45JVWUg/view?usp=sharing"
  },
  "Drums": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 99 to 131, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/1Mpdfp3rIUnuBnqOlEcWf93pCzvGNvldp/view?usp=sharing"
  },
  "Piano": {
    piece: "Christmas Eve/Sarajevo",
    measures: "Compas 139 to 168, Negra 160 (BPM 160)",
    musicLink: "https://drive.google.com/file/d/1zNAPQszUwuHbhtdUvScL2ownPqaeykkD/view?usp=sharing"
  },
};

const AuditionForm = ({
  uid,
  name,
  concertRepertoire = defaultConcertRepertoire,
  instrumentExcerpts = defaultInstrumentExcerpts,
}) => {
  const [youtubeLink, setYoutubeLink] = useState('');
  const [error, setError] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);

  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  // Check if the current user has already applied (auditionPaid is true)
  useEffect(() => {
    const checkApplicationStatus = async () => {
      if (!uid) return;
      try {
        const reservationRef = doc(db, "reservations", uid);
        const reservationSnap = await getDoc(reservationRef);
        if (reservationSnap.exists()) {
          const data = reservationSnap.data();
          if (data.auditionPaid === true) {
            setHasApplied(true);
          }
        }
      } catch (err) {
        console.error("Error fetching reservation:", err);
      }
    };
    checkApplicationStatus();
  }, [uid]);

  // If the user has already applied, show a celebratory congratulatory message.
  if (hasApplied) {
    return (
      <div className="bg-gray-800 min-h-screen flex items-center justify-center">
        <div className="max-w-2xl mx-auto p-8 bg-gray-900 shadow-lg rounded-lg text-center">
          <h1 className="text-3xl font-bold mb-6 text-yellow-400">Congratulations!</h1>
          <p className="mb-4 text-gray-200">
            You have successfully applied for the Siman Orchestral Foundation tour!
          </p>
          <p className="mb-4 text-gray-200">
            Get ready to embark on an exciting musical journey in New York City. Your audition is confirmed and you'll receive your results in 72 Hours.
          </p>
          <p className="mb-4 text-gray-200">
            We can’t wait to see you shine on stage and be a part of this unforgettable experience!
          </p>
        </div>
      </div>
    );
  }

  // Validate a YouTube link using regex
  const isValidYouTubeLink = (url) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
    return youtubeRegex.test(url);
  };

  // Handle audition info submission (before showing payment panel)
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValidYouTubeLink(youtubeLink)) {
      setError('Please enter a valid YouTube link.');
      return;
    }
    setError('');
    setShowPayment(true);
  };

  // Handle the payment process
  const handlePayment = async () => {
    if (!stripe || !elements) {
      alert("Stripe has not loaded yet. Please try again later.");
      return;
    }
    setLoading(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user || !user.email) {
        alert("User is not signed in or email is unavailable. Please sign in first.");
        setLoading(false);
        return;
      }
      
      // Prepare payment data
      const paymentData = {
        amount: 65, // $65 fee
        uid,
        youtubeLink,
        email: user.email,
        name,
      };

      // 1) Create PaymentIntent via HTTP endpoint (note the /api/ prefix)
      const response = await fetch(
        "https://us-central1-newyorktour-33c8c.cloudfunctions.net/api/createPaymentIntent",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(paymentData),
        }
      );

      if (!response.ok) {
        alert("Error creating Payment Intent. Please try again.");
        setLoading(false);
        return;
      }
      const result = await response.json();
      const clientSecret = result.clientSecret;
      
      // 2) Get the CardElement for card details
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        alert("Card input not found. Please try again.");
        setLoading(false);
        return;
      }

      // 3) Confirm the card payment using Stripe
      const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name,
              email: user.email,
            },
          },
        }
      );

      if (confirmError) {
        console.error("Error confirming card payment:", confirmError);
        alert("Error confirming payment. Please try again.");
        setLoading(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        // Payment succeeded – update Firestore with the audition info
        await updateDoc(doc(db, "reservations", uid), {
          auditionLink: youtubeLink,
          auditionPaid: true,
        });
        alert("Payment successful and audition info saved!");
        // Instead of navigating away, update the local state to trigger a re-render.
        setHasApplied(true);
        // Alternatively, to force a full reload you could call: window.location.reload();
      }
    } catch (err) {
      console.error("Payment error:", err);
      alert("There was an error processing your payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-800 min-h-screen text-gray-100">
      <div className="max-w-3xl mx-auto p-8 mt-10 bg-gray-900 shadow-lg rounded-lg">
        <h1 className="text-3xl font-bold mb-6 text-center text-yellow-400">
        ¡Bienvenido!{name || "Invitadot"}
        </h1>
        <p className="mb-4 text-center text-gray-200">
      Prepárate para unirte a la <strong>Fundación Orquestal Siman</strong> en su gira por <strong>la ciudad de Nueva York</strong>.
    </p>
        <h2 className="text-lg font-semibold mb-2 text-yellow-400">Application Fee:</h2>
        <ul className="mb-4 text-gray-200 space-y-3">
        <li>
        🎵 <strong>1 de abril – 20 de mayo</strong>: Inscripción – <span className="font-bold">$65</span>
      </li>
      <li className="bg-gray-800 text-gray-400 text-sm p-3 rounded-md border border-gray-700 leading-relaxed">
        <strong>Nota:</strong> Las solicitudes enviadas después del <span className="text-white font-semibold">20 de mayo</span> serán evaluadas caso por caso. Si son aprobadas, deberás cubrir todos los pagos mensuales anteriores para mantenerte al corriente con el calendario de la gira.
        <br /><br />
        El costo total del tour puede aumentar dependiendo de qué tan tarde se envíe tu solicitud.
      </li>
        </ul>
        <p className="text-red-400 font-semibold text-sm mb-4">
      ⚠️ Las tarifas de inscripción son <strong>no transferibles y no reembolsables.</strong>
    </p>
    <p className="mb-4 text-gray-200">
      <strong>Fechas del Tour:</strong> 15 al 20 de diciembre, Ciudad de Nueva York
    </p>
    <p className="mb-6 text-gray-200">
      Para completar tu solicitud, debes enviar tu video de audición y realizar el pago en el mismo paso.
    </p>
    <h2 className="text-3xl font-serif font-bold mb-6 text-yellow-700">
      Repertorio del Concierto
    </h2>
        <ul className="list-disc list-inside text-left max-w-xl mx-auto text-gray-200 space-y-2 mb-8">
          {concertRepertoire.map((piece, index) => (
            <li key={index}>{piece}</li>
          ))}
        </ul>
        <h2 className="text-xl font-semibold mb-4 text-yellow-400">
        🎻 Elige tu instrumento:
        </h2>
        <div className="grid grid-cols-2 gap-4 mb-6">
          {Object.keys(defaultInstrumentExcerpts).map((instrument) => (
            <button
              key={instrument}
              onClick={() => setSelectedInstrument(instrument)}
              className={`bg-gray-700 hover:bg-yellow-600 text-gray-100 py-2 rounded transition-colors ${
                selectedInstrument === instrument ? "ring-2 ring-yellow-400" : ""
              }`}
            >
              {instrument}
            </button>
          ))}
        </div>
        {selectedInstrument && (
          <motion.div
            className="p-4 bg-gray-700 rounded-lg shadow-md mb-8"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <h3 className="text-lg font-semibold mb-2 text-yellow-400">
            🎼 Fragmento de Audición para {selectedInstrument}
            </h3>
            <p className="text-gray-200">
              <strong>Pieza:</strong> {defaultInstrumentExcerpts[selectedInstrument].piece}
            </p>
            <p className="text-gray-200">
              <strong>Compases:</strong> {defaultInstrumentExcerpts[selectedInstrument].measures}
            </p>
            <a 
              href={defaultInstrumentExcerpts[selectedInstrument].musicLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:underline"
            >
           Ver partitura en Google Drive
            </a>
          </motion.div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Enter YouTube link..."
            value={youtubeLink}
            onChange={(e) => setYoutubeLink(e.target.value)}
            className="border border-gray-700 bg-gray-800 p-3 rounded w-full text-gray-100 placeholder-gray-400"
          />
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button
            type="submit"
            className="bg-yellow-600 text-gray-900 px-6 py-3 rounded w-full hover:bg-yellow-500 transition-colors font-semibold"
            disabled={loading}
          >
            {loading ? "Processing..." : "Submit & Proceed to Payment"}
          </button>
        </form>
        {showPayment && (
          <motion.div
            className="mt-8 px-4 py-6 sm:p-6 bg-gray-700 rounded-lg shadow-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-xl font-semibold mb-4 text-yellow-400">
            🎟️ Pago de Inscripción
            </h2>
            <p className="text-gray-200 mb-4">
            Estás a punto de pagar <strong>$65</strong>.
            </p>
            <div className="bg-gray-800 p-4 rounded mb-4 w-full">
  <CardElement
    options={{
      style: {
        base: {
          fontSize: window.innerWidth < 400 ? '13px' : '16px',
          color: '#ffffff',
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          lineHeight: '24px',
          '::placeholder': {
            color: '#a0aec0',
          },
        },
        invalid: {
          color: '#e53e3e',
        },
      },
    }}
  />
</div>


            <button
              onClick={handlePayment}
              className="bg-yellow-600 text-gray-900 px-6 py-3 rounded w-full hover:bg-yellow-500 transition-colors font-semibold disabled:opacity-50"
              disabled={loading}
            >
              {loading ? "Processing..." : "Proceed to Pay $65"}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AuditionForm;
